// Sample lineage for the sample store (store-fake.ts, offline page work only): the Halloween pumpkin with five
// versions (one yanked, one merged from a fork), four forks under other creators (one a fork of a fork), one merged
// and one open offer back, and the source hunks a "what changed" shows. Pure data and functions, so tests read it with
// the real asset.json from store/packages.

import type { Manifest, ParamSchema, ReviewStatus } from "./store.ts";
import type { FileChange, Hunk, ParamChange, Proposal } from "./lineage.ts";

export const DAY = 86400;
export const T0 = Date.UTC(2026, 9, 1) / 1000;

export interface SampleVersion {
  version: string;
  status: ReviewStatus;
  /** unix seconds */
  at: number;
  note?: string;
  yankReason?: string;
  /** the fork version a merge copied in */
  mergedFrom?: string;
  /** edits applied to the package's base manifest for this version */
  edit?: (m: Manifest) => Manifest;
}

export interface SampleFork {
  id: string;
  /** "<id>@<version>" it was forked from */
  parent: string;
  title: string;
  versions: SampleVersion[];
}

const clone = (m: Manifest): Manifest => structuredClone(m);
const props = (m: Manifest): Record<string, ParamSchema> => (m.params ??= {}).properties ??= {};
/** a new param goes into the creator's order too, the way a creator adds one */
const addParam = (m: Manifest, key: string, schema: ParamSchema): void => {
  props(m)[key] = schema;
  if (Array.isArray(m.params!.order) && !m.params!.order.includes(key)) m.params!.order = [...m.params!.order, key];
};

export const PUMPKIN = "threedom/halloween-pumpkin";

// the real asset.json is the newest version; older ones take features away
const v120 = (m: Manifest): Manifest => {
  const x = clone(m);
  const p = props(x);
  if (p.glow) p.glow.default = 1.2;
  if (p.ribDepth) p.ribDepth.maximum = 0.18;
  return x;
};
const v110 = (m: Manifest): Manifest => {
  const x = v120(m);
  const p = props(x);
  delete p.light;
  delete p.lightIntensity;
  if (p.squash) p.squash.maximum = 1;
  return x;
};
const v100 = (m: Manifest): Manifest => {
  const x = v110(m);
  const p = props(x);
  delete p.teeth;
  if (p.face?.enum) p.face.enum = p.face.enum.filter((f) => f !== "surprised");
  x.tags = (x.tags ?? []).filter((t) => t !== "glow");
  return x;
};

/** The pumpkin's history, oldest first. */
export const PUMPKIN_HISTORY: SampleVersion[] = [
  { version: "1.0.0", status: "listed", at: T0 + 1 * DAY, edit: v100 },
  { version: "1.1.0", status: "listed", at: T0 + 4 * DAY, edit: v110 },
  {
    version: "1.1.1",
    status: "yanked",
    at: T0 + 6 * DAY,
    yankReason: "At 6 teeth the cuts went through the back of the pumpkin. Fixed in 1.2.0.",
    edit: (m) => {
      const x = v110(m);
      if (props(x).teeth) props(x).teeth.maximum = 8;
      return x;
    },
  },
  { version: "1.2.0", status: "listed", at: T0 + 10 * DAY, mergedFrom: "mira/tall-pumpkin@1.2.0", edit: v120 },
  { version: "1.3.0", status: "listed", at: T0 + 15 * DAY },
];

/** Forks, parents before their own forks. Each fork's base is its parent's manifest at the forked version. */
export const SAMPLE_FORKS: SampleFork[] = [
  {
    id: "mira/tall-pumpkin",
    parent: `${PUMPKIN}@1.1.0`,
    title: "Tall Pumpkin",
    versions: [
      {
        version: "1.1.0",
        status: "listed",
        at: T0 + 5 * DAY,
        edit: (m) => {
          const x = clone(m);
          if (props(x).squash) Object.assign(props(x).squash!, { maximum: 1.15, default: 0.95 });
          return x;
        },
      },
      {
        version: "1.2.0",
        status: "listed",
        at: T0 + 8 * DAY,
        edit: (m) => {
          const x = clone(m);
          const p = props(x);
          if (p.squash) Object.assign(p.squash, { maximum: 1.15, default: 0.95 });
          addParam(x, "light", { type: "boolean", default: false, description: "a real light, so the pumpkin lights the room around it" });
          addParam(x, "lightIntensity", { type: "number", minimum: 0, maximum: 10, default: 1.5, description: "how bright that light is" });
          return x;
        },
      },
    ],
  },
  {
    id: "oskar/pumpkin-lantern",
    parent: `${PUMPKIN}@1.2.0`,
    title: "Pumpkin Lantern",
    versions: [
      {
        version: "1.2.0",
        status: "listed",
        at: T0 + 11 * DAY,
        edit: (m) => {
          const x = clone(m);
          const p = props(x);
          addParam(x, "handle", { type: "boolean", default: true, description: "a wire handle on top, to carry it" });
          if (p.colour) p.colour.default = "#d9661c";
          return x;
        },
      },
      {
        version: "1.3.0",
        status: "listed",
        at: T0 + 13 * DAY,
        edit: (m) => {
          const x = clone(m);
          const p = props(x);
          addParam(x, "handle", { type: "boolean", default: true, description: "a wire handle on top, to carry it" });
          addParam(x, "handleColour", { type: "string", format: "color", default: "#2b2b2b", description: "the handle's colour" });
          if (p.colour) p.colour.default = "#d9661c";
          return x;
        },
      },
    ],
  },
  {
    id: "juno/ghost-pumpkin",
    parent: `${PUMPKIN}@1.3.0`,
    title: "Ghost Pumpkin",
    versions: [
      {
        version: "1.3.0",
        status: "listed",
        at: T0 + 16 * DAY,
        edit: (m) => {
          const x = clone(m);
          const p = props(x);
          if (p.colour) p.colour.default = "#ece6d6";
          if (p.stem) p.stem.default = "#6b6b5a";
          if (p.face) p.face.default = "scary";
          return x;
        },
      },
    ],
  },
  {
    id: "kit/mini-lantern",
    parent: "oskar/pumpkin-lantern@1.3.0",
    title: "Mini Lantern",
    versions: [
      {
        version: "1.3.0",
        status: "listed",
        at: T0 + 17 * DAY,
        edit: (m) => {
          const x = clone(m);
          if (props(x).width) props(x).width.default = 0.2;
          return x;
        },
      },
    ],
  },
];

/** Offers back: one merged into the pumpkin's 1.2.0, one waiting for its creator. */
export const SAMPLE_PROPOSALS: Proposal[] = [
  {
    id: "pr_sample_open",
    package: PUMPKIN,
    from: "oskar/pumpkin-lantern@1.3.0",
    fromPackage: "oskar/pumpkin-lantern",
    fromHandle: "oskar",
    title: "A wire handle so you can carry it",
    message: "Adds a handle you can turn on or off, and its colour. It starts on in my version; happy for it to start off in yours.",
    status: "open",
    mergedVersion: null,
    mergedRef: null,
    note: null,
    createdAt: T0 + 14 * DAY,
    decidedAt: null,
  },
  {
    id: "pr_sample_merged",
    package: PUMPKIN,
    from: "mira/tall-pumpkin@1.2.0",
    fromPackage: "mira/tall-pumpkin",
    fromHandle: "mira",
    title: "Light the room, and taller pumpkins",
    message: "A real light so the pumpkin lights what's around it, and squash up to 1.15 for tall ones.",
    status: "merged",
    mergedVersion: "1.2.0",
    mergedRef: `${PUMPKIN}@1.2.0`,
    note: "Lovely. Thank you, Mira!",
    createdAt: T0 + 8 * DAY + 3600 * 5,
    decidedAt: T0 + 10 * DAY,
  },
];

/** The store's fork score (backend/store forkList): merged changes, its own forks, versions, recent activity. */
export function forkScore(f: { merged: number; forks: number; versions: number; updatedAt: number }, now: number): number {
  return f.merged * 10 + f.forks * 3 + Math.min(f.versions, 10) + ((now - f.updatedAt) / DAY < 30 ? 2 : 0);
}

const lit = (v: unknown): string => (typeof v === "string" ? JSON.stringify(v) : String(v));

/**
 * Believable source hunks for the adapter (dist/three.js) from the param changes: the DEFAULTS and RANGES tables at the
 * top of a smart asset's code. Empty when no param changed.
 */
export function sampleHunks(changes: ParamChange[]): Hunk[] {
  const defs: string[] = [];
  const ranges: string[] = [];
  const schema = (v: unknown) => (v && typeof v === "object" ? (v as ParamSchema) : {});
  for (const c of changes) {
    if (c.change === "added") {
      const s = schema(c.after);
      defs.push(`+  ${c.name}: ${lit(s.default ?? null)},`);
      if (typeof s.minimum === "number") ranges.push(`+  ${c.name}: [${s.minimum}, ${s.maximum}],`);
    } else if (c.change === "removed") {
      const s = schema(c.before);
      defs.push(`-  ${c.name}: ${lit(s.default ?? null)},`);
      if (typeof s.minimum === "number") ranges.push(`-  ${c.name}: [${s.minimum}, ${s.maximum}],`);
    } else {
      const f = c.fields ?? {};
      if (f.default) defs.push(`-  ${c.name}: ${lit(f.default[0])},`, `+  ${c.name}: ${lit(f.default[1])},`);
      if (f.minimum || f.maximum) {
        const lo = f.minimum ?? [null, null];
        const hi = f.maximum ?? [null, null];
        ranges.push(`-  ${c.name}: [${lo[0] ?? "min"}, ${hi[0] ?? "max"}],`, `+  ${c.name}: [${lo[1] ?? "min"}, ${hi[1] ?? "max"}],`);
      }
    }
  }
  const hunk = (start: number, head: string, body: string[]): Hunk => {
    const lines = [` ${head}`, ...body, " };"];
    return { a: [start, lines.filter((l) => l[0] !== "+").length], b: [start, lines.filter((l) => l[0] !== "-").length], lines };
  };
  const out: Hunk[] = [];
  if (defs.length) out.push(hunk(14, "const DEFAULTS = {", defs));
  if (ranges.length) out.push(hunk(40, "const RANGES = {", ranges));
  return out;
}

/** The files a sample diff lists: the adapter's hunks, and the picture and ready-made model as binary. */
export function sampleFileChanges(changes: ParamChange[], anyChange: boolean): FileChange[] {
  if (!anyChange) return [];
  const hunks = sampleHunks(changes);
  const out: FileChange[] = [];
  if (hunks.length) out.push({ path: "dist/three.js", change: "changed", size: [24_310, 24_310 + hunks.length * 64], hunks });
  out.push({ path: "baked/default.glb", change: "changed", size: [182_204, 186_910], skipped: "binary" });
  out.push({ path: "preview.png", change: "changed", size: [96_412, 98_007], skipped: "binary" });
  return out;
}
